import type { VenueIndexData } from '../dto/EntityRepository';
import { Language } from '../enum/EnumRepository';
import {
  LANDING_COLUMN_GAP2,
  LANDING_COLUMN_WIDTH2,
  LANDING_COLUMN_WIDTH2_MOBILE,
  getLandingColumnGap2Px,
  getLandingColumnWidth2Px,
  type LandingArchiveGroup,
} from './landingArchiveIndex';
import { getVenueDisplayName } from './venueTypeLabel';

export type ExhibitionVenueCandidate = {
  id: string;
  name: string;
};

export { LANDING_COLUMN_GAP2, LANDING_COLUMN_WIDTH2, LANDING_COLUMN_WIDTH2_MOBILE };

/** 데스크톱: 열당 선호 행 수. 넘치면 다음 열로, 최대 열 수에 맞춰 행 수를 늘린다. */
export const EXHIBITION_VENUE_INDEX_PREFERRED_ROWS_DESKTOP = 35;
export const EXHIBITION_VENUE_INDEX_MAX_COLUMNS_DESKTOP = 6;
export const EXHIBITION_VENUE_INDEX_MAX_COLUMNS_MOBILE = 2;
export const INTEREST_EXHIBITION_CONTAINER_MIN_WIDTH = 280;
export const INTEREST_EXHIBITION_CONTAINER_MAX_WIDTH = 380;
export const EXHIBITION_VENUE_LAYOUT_GAP = 10;

export const getExhibitionVenueIndexMaxColumns = (
  availableWidth: number,
  viewportWidth: number,
  hasInterestContainer: boolean,
): number => {
  if (availableWidth <= 0) return EXHIBITION_VENUE_INDEX_MAX_COLUMNS_DESKTOP;

  const columnWidth = getLandingColumnWidth2Px(viewportWidth);
  const columnGap = getLandingColumnGap2Px(viewportWidth);
  const interestContainerWidth = hasInterestContainer
    ? INTEREST_EXHIBITION_CONTAINER_MIN_WIDTH + EXHIBITION_VENUE_LAYOUT_GAP
    : 0;
  const indexWidth = Math.max(0, availableWidth - interestContainerWidth);
  const columnCount = Math.floor((indexWidth + columnGap) / (columnWidth + columnGap));

  return Math.max(1, Math.min(EXHIBITION_VENUE_INDEX_MAX_COLUMNS_DESKTOP, columnCount));
};

const HANGUL_SYLLABLE_START = 0xac00;
const HANGUL_SYLLABLE_END = 0xd7a3;

const CHOSEONG_BY_INDEX = [
  'ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ',
] as const;

const CHOSEONG_GROUP_KEY: Record<string, string> = {
  'ㄱ': 'ㄱ', 'ㄲ': 'ㄱ', 'ㄴ': 'ㄴ', 'ㄷ': 'ㄷ', 'ㄸ': 'ㄷ', 'ㄹ': 'ㄹ', 'ㅁ': 'ㅁ',
  'ㅂ': 'ㅂ', 'ㅃ': 'ㅂ', 'ㅅ': 'ㅅ', 'ㅆ': 'ㅅ', 'ㅇ': 'ㅇ', 'ㅈ': 'ㅈ', 'ㅉ': 'ㅈ',
  'ㅊ': 'ㅊ', 'ㅋ': 'ㅋ', 'ㅌ': 'ㅌ', 'ㅍ': 'ㅍ', 'ㅎ': 'ㅎ',
};

const LATIN_GROUP_ORDER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const KOREAN_GROUP_ORDER = ['ㄱ', 'ㄴ', 'ㄷ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅅ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'] as const;

const getMergedChoseongFromCodePoint = (codePoint: number): string | null => {
  if (codePoint < HANGUL_SYLLABLE_START || codePoint > HANGUL_SYLLABLE_END) return null;
  const choseongIndex = Math.floor((codePoint - HANGUL_SYLLABLE_START) / 588);
  const jamo = CHOSEONG_BY_INDEX[choseongIndex];
  return jamo ? CHOSEONG_GROUP_KEY[jamo] ?? null : null;
};

const getGroupKey = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return '#';

  const firstUnit = trimmed.charAt(0);
  if (/^[a-zA-Z]$/.test(firstUnit)) {
    return firstUnit.toUpperCase();
  }

  const cp = trimmed.codePointAt(0);
  if (cp === undefined) return '#';

  const hangulKey = getMergedChoseongFromCodePoint(cp);
  if (hangulKey) return hangulKey;

  return '#';
};

const getGroupSortIndex = (key: string): number => {
  const latinIdx = LATIN_GROUP_ORDER.indexOf(key);
  if (latinIdx !== -1) return latinIdx;

  const koIdx = (KOREAN_GROUP_ORDER as readonly string[]).indexOf(key);
  if (koIdx !== -1) return 100 + koIdx;

  return 200;
};

/** Artist Index와 동일: A→Aa, ㄱ→ㄱ, #→## */
const getGroupLabel = (key: string): string => {
  if (key === '#') return '##';
  if (/^[A-Z]$/.test(key)) return `${key}${key.toLowerCase()}`;
  return key;
};

export const toExhibitionVenueCandidate = (
  venue: VenueIndexData,
  language: Language,
): ExhibitionVenueCandidate => ({
  id: String(venue.id),
  name: getVenueDisplayName(venue, language),
});

const buildGroupedCandidates = (
  candidates: ExhibitionVenueCandidate[],
): LandingArchiveGroup[] => {
  const sorted = [...candidates].sort((a, b) => {
    const ka = getGroupKey(a.name);
    const kb = getGroupKey(b.name);
    const ia = getGroupSortIndex(ka);
    const ib = getGroupSortIndex(kb);
    if (ia !== ib) return ia - ib;
    return a.name.localeCompare(b.name, 'ko', { sensitivity: 'base' });
  });

  return sorted.reduce<LandingArchiveGroup[]>((groups, candidate) => {
    const letter = getGroupKey(candidate.name);
    const existingGroup = groups[groups.length - 1];

    if (existingGroup && existingGroup.letter === letter) {
      existingGroup.candidates.push({
        username: candidate.name,
        aui: candidate.id,
        handle: '',
      });
      return groups;
    }

    groups.push({
      letter,
      label: getGroupLabel(letter),
      candidates: [{
        username: candidate.name,
        aui: candidate.id,
        handle: '',
      }],
    });

    return groups;
  }, []);
};

const countGroupRows = (group: LandingArchiveGroup): number =>
  group.candidates.length + (group.label ? 1 : 0);

const splitGroupsIntoColumns = (
  groups: LandingArchiveGroup[],
  maxRowsPerColumn: number,
): LandingArchiveGroup[][] =>
  groups.reduce<LandingArchiveGroup[][]>((columns, group) => {
    const groupRowCount = countGroupRows(group);
    const currentColumn = columns[columns.length - 1];
    const currentRowCount = currentColumn.reduce(
      (total, currentGroup) => total + countGroupRows(currentGroup),
      0,
    );

    if (currentColumn.length === 0 || currentRowCount + groupRowCount <= maxRowsPerColumn) {
      currentColumn.push(group);
      return columns;
    }

    columns.push([group]);
    return columns;
  }, [[]]);

export type BuildExhibitionVenueIndexColumnsOptions = {
  preferredRowsPerColumn?: number;
  maxColumns?: number;
  language: Language;
};

export const buildExhibitionVenueIndexGroups = (
  venues: VenueIndexData[],
  language: Language,
): LandingArchiveGroup[] => {
  if (venues.length === 0) return [];
  return buildGroupedCandidates(venues.map((venue) => toExhibitionVenueCandidate(venue, language)));
};

/**
 * 데스크톱: 선호 행 수로 왼쪽부터 채움 (그룹 단위, 라벨 행 포함).
 * 그룹을 통째로 옮기면 이론상 열 수를 넘길 수 있어, 넘치면 열당 행을 늘려 최대 열 수에 맞춘다.
 */
export const buildExhibitionVenueIndexColumns = (
  venues: VenueIndexData[],
  options: BuildExhibitionVenueIndexColumnsOptions,
): LandingArchiveGroup[][] => {
  const groups = buildExhibitionVenueIndexGroups(venues, options.language);
  if (groups.length === 0) return [];

  const preferredRows =
    options.preferredRowsPerColumn ?? EXHIBITION_VENUE_INDEX_PREFERRED_ROWS_DESKTOP;
  const maxColumns =
    options.maxColumns ?? EXHIBITION_VENUE_INDEX_MAX_COLUMNS_DESKTOP;
  const totalRows = groups.reduce((total, group) => total + countGroupRows(group), 0);

  let maxRowsPerColumn = Math.max(
    preferredRows,
    Math.ceil(totalRows / maxColumns),
  );
  let columns = splitGroupsIntoColumns(groups, maxRowsPerColumn);

  while (columns.length > maxColumns && maxRowsPerColumn < totalRows) {
    maxRowsPerColumn += 1;
    columns = splitGroupsIntoColumns(groups, maxRowsPerColumn);
  }

  return columns;
};

/** 모바일: 그룹을 깨지 않고 최대 2열로 행 수 기준 분할 */
export const splitExhibitionVenueIndexTwoColumns = (
  groups: LandingArchiveGroup[],
): LandingArchiveGroup[][] => {
  if (groups.length === 0) return [];

  const totalRows = groups.reduce((total, group) => total + countGroupRows(group), 0);
  const targetRows = Math.ceil(totalRows / EXHIBITION_VENUE_INDEX_MAX_COLUMNS_MOBILE);
  const left: LandingArchiveGroup[] = [];
  const right: LandingArchiveGroup[] = [];
  let leftRows = 0;

  groups.forEach((group) => {
    const groupRowCount = countGroupRows(group);
    if (right.length === 0 && (left.length === 0 || leftRows + groupRowCount <= targetRows)) {
      left.push(group);
      leftRows += groupRowCount;
      return;
    }
    right.push(group);
  });

  return right.length > 0 ? [left, right] : [left];
};
